import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Shield, ShieldCheck } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useCurrentAdmin } from '../../lib/useCurrentAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = { username: '', email: '', password: '', full_name: '', role: 'editor' };

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { me } = useCurrentAdmin();

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users`, { headers });
      setUsers(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users`, form, { headers });
      toast.success(`User "${form.username}" created (${form.role})`);
      setIsOpen(false);
      setForm(initialForm);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (username, newRole) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${encodeURIComponent(username)}/role`,
        { role: newRole },
        { headers },
      );
      toast.success(`Role updated for ${username}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Role update failed');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${encodeURIComponent(pendingDelete.username)}`,
        { headers },
      );
      toast.success(`User "${pendingDelete.username}" deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-7 w-7" style={{ color: '#00CED1' }} />
              Manage Users
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Admins have full access. Editors can manage content (Projects, Products, Services) and restore from Trash, but cannot change order stages, delete contacts, permanently purge, or manage other users.
            </p>
          </div>
          <Button
            onClick={() => { setForm(initialForm); setIsOpen(true); }}
            className="text-white font-semibold"
            style={{ backgroundColor: '#00CED1' }}
            data-testid="admin-users-add-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading users…</p>
        ) : users.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No users yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {users.map((u) => {
              const isMe = me && me.username === u.username;
              return (
                <Card key={u.username} data-testid={`admin-users-row-${u.username}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                      style={{ backgroundColor: u.role === 'admin' ? '#00CED1' : '#a16207' }}
                    >
                      {(u.full_name || u.username)[0].toUpperCase()}
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{u.full_name || u.username}</h3>
                        <Badge
                          variant="outline"
                          className="capitalize"
                          style={u.role === 'admin'
                            ? { borderColor: '#00CED1', color: '#00CED1' }
                            : { borderColor: '#a16207', color: '#a16207' }}
                          data-testid={`admin-users-role-badge-${u.username}`}
                        >
                          {u.role === 'admin' ? <ShieldCheck className="h-3 w-3 mr-1 inline" /> : <Shield className="h-3 w-3 mr-1 inline" />}
                          {u.role}
                        </Badge>
                        {isMe && (
                          <Badge className="text-[10px]" style={{ backgroundColor: '#00CED1' }}>You</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">@{u.username} • {u.email}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Select
                        value={u.role}
                        onValueChange={(v) => handleRoleChange(u.username, v)}
                        disabled={isMe}
                      >
                        <SelectTrigger className="w-32 h-9" data-testid={`admin-users-role-select-${u.username}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                        </SelectContent>
                      </Select>
                      {!isMe && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingDelete(u)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`admin-users-delete-${u.username}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create user modal */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <Label>Username *</Label>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  required
                  minLength={3}
                  data-testid="admin-users-username-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="admin-users-email-input"
                />
              </div>
              <div>
                <Label>Full name</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="admin-users-fullname-input"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="admin-users-password-input"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="admin-users-new-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="editor">Editor (content-only)</SelectItem>
                    <SelectItem value="admin">Admin (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 text-white"
                  style={{ backgroundColor: '#00CED1' }}
                  data-testid="admin-users-submit"
                >
                  {submitting ? 'Creating…' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  className="flex-1"
                  data-testid="admin-users-cancel"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={!!pendingDelete}
          title={pendingDelete ? `Delete user "${pendingDelete.username}"?` : 'Delete user?'}
          description="They will lose access immediately. Their content edits remain in version history."
          confirmLabel="Delete"
          destructive
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          testId="admin-users-confirm-delete"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
